import React, { useEffect, createElement } from 'react';
import { MotionConfig } from 'framer-motion';
import { Landing } from './pages/Landing';
import { useScreenInit } from './useScreenInit';
const META_PIXEL_ID = '3505657626270665';
const PAGE_TITLE = 'KIT OPERATIVO PARA PSICÓLOGOS — Sistema Integral de Plantillas, Scripts y Protocolos';
const PAGE_DESC = 'Todo lo que necesitás para gestionar tu consultorio ya está listo: formularios, scripts, protocolos, modelos de informes y estructuras listas para usar desde el primer día. Para psicólogos en ejercicio independiente.';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/mCKu1Fo4Kepf92sxZPJhA7/Texto_del_parrafo_(11).webp';
const SITE_URL = 'https://kit-operativo-para-psicologos.com';
function setMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
function addLink(rel: string, href: string, attrs: Record<string, string> = {}) {
  const selector = `link[rel="${rel}"][href="${href}"]`;
  if (document.head.querySelector(selector)) return;
  const el = document.createElement('link');
  el.rel = rel;
  el.href = href;
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  document.head.appendChild(el);
}
export function App() {
  useScreenInit();
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = 'es-AR';
    document.title = PAGE_TITLE;
    addLink('preconnect', 'https://cdn.magicpatterns.com', {
      crossorigin: ''
    });
    addLink('preconnect', 'https://fonts.googleapis.com');
    addLink('preconnect', 'https://fonts.gstatic.com', {
      crossorigin: ''
    });
    addLink('preload', HERO_IMAGE, {
      as: 'image',
      fetchpriority: 'high'
    });
    addLink('canonical', SITE_URL);
    setMeta('name', 'description', PAGE_DESC);
    setMeta('name', 'viewport', 'width=device-width, initial-scale=1, viewport-fit=cover');
    setMeta('name', 'theme-color', '#F9F8F6');
    setMeta('name', 'robots', 'index, follow');
    setMeta('property', 'og:type', 'website');
    setMeta('property', 'og:title', PAGE_TITLE);
    setMeta('property', 'og:description', PAGE_DESC);
    setMeta('property', 'og:locale', 'es_AR');
    setMeta('property', 'og:image', HERO_IMAGE);
    setMeta('property', 'og:url', SITE_URL);
    setMeta('property', 'og:site_name', 'Kit Operativo para Psicólogos');
    setMeta('name', 'twitter:card', 'summary_large_image');
    setMeta('name', 'twitter:image', HERO_IMAGE);
    setMeta('name', 'twitter:title', PAGE_TITLE);
    setMeta('name', 'twitter:description', PAGE_DESC);
    const ldId = 'ld-product-schema';
    if (!document.getElementById(ldId)) {
      const ld = document.createElement('script');
      ld.id = ldId;
      ld.type = 'application/ld+json';
      ld.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Product',
        name: 'Kit Operativo para Psicólogos',
        description: PAGE_DESC,
        image: HERO_IMAGE,
        brand: {
          '@type': 'Brand',
          name: 'Kit Operativo para Psicólogos'
        },
        offers: {
          '@type': 'Offer',
          price: '19900',
          priceCurrency: 'ARS',
          availability: 'https://schema.org/InStock',
          url: 'https://kit-operativo-para-psicologos.impultienda.ar/checkout'
        }
      });
      document.head.appendChild(ld);
    }
    const faqId = 'ld-faq-schema';
    if (!document.getElementById(faqId)) {
      const faq = document.createElement('script');
      faq.id = faqId;
      faq.type = 'application/ld+json';
      faq.textContent = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [{
          q: '¿Es en papel o digital?',
          a: 'Es 100% digital. Recibís todo de forma inmediata en tu correo, listo para usar, imprimir o completar en pantalla.'
        }, {
          q: '¿Necesito experiencia previa para usarlo?',
          a: 'No. Está diseñado para que cualquier psicólogo pueda abrirlo y empezar desde el primer día.'
        }, {
          q: '¿Sirve para cualquier orientación teórica?',
          a: 'Sí. El contenido es operativo y administrativo, no clínico-teórico. Sirve sin importar tu orientación.'
        }, {
          q: '¿Qué pasa si ya tengo algunos documentos armados?',
          a: 'El kit complementa lo que ya tenés y cubre los vacíos que todavía no resolviste.'
        }, {
          q: '¿Cuándo recibo el material?',
          a: 'De forma inmediata. En el momento en que confirmás tu pago, recibís el acceso completo en tu correo.'
        }, {
          q: '¿Sirve si recién me recibí?',
          a: 'Es ideal para empezar bien desde el principio y ahorrarte meses de prueba y error en la gestión.'
        }].map(item => ({
          '@type': 'Question',
          name: item.q,
          acceptedAnswer: {
            '@type': 'Answer',
            text: item.a
          }
        }))
      });
      document.head.appendChild(faq);
    }
  }, []);
  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // Solo se carga el Meta Pixel real (3505657626270665).
    if (!META_PIXEL_ID) return;
    const loadPixel = () => {
      const w = window as any;
      if (w.fbq) {
        w.fbq('track', 'PageView');
        return;
      }
      const n: any = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      };
      n.push = n;
      n.loaded = true;
      n.version = '2.0';
      n.queue = [];
      w.fbq = n;
      if (!w._fbq) w._fbq = n;
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const target = document.head || document.body || document.documentElement;
      target.appendChild(script);
      w.fbq('init', META_PIXEL_ID);
      w.fbq('track', 'PageView');
      const noscript = document.createElement('noscript');
      const img = document.createElement('img');
      img.height = 1;
      img.width = 1;
      img.style.display = 'none';
      img.alt = '';
      img.src = `https://www.facebook.com/tr?id=${META_PIXEL_ID}&ev=PageView&noscript=1`;
      noscript.appendChild(img);
      target.appendChild(noscript);
    };
    const ric = (window as any).requestIdleCallback as ((cb: () => void, opts?: {
      timeout: number;
    }) => number) | undefined;
    if (ric) {
      ric(loadPixel, {
        timeout: 4000
      });
    } else {
      const t = setTimeout(loadPixel, 2500);
      return () => clearTimeout(t);
    }
  }, []);
  return <MotionConfig reducedMotion="user" data-id="element-0">
      <Landing data-id="element-1" />
    </MotionConfig>;
}